'use client';

import { type FC, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { BonusShopProductType } from '@/api/cms/bonusShop';
import { Button, ButtonClose } from '@/components/buttons';
import { ImageComponent } from '@/components/Image';
import { InfoMessage, WarningMessage } from '@/components/Message';
import { Modal } from '@/components/Modal';
import { StaticModal } from '@/components/StaticModal';
import { blurImagePlaceholder } from '@/theme/placeholders';
import { formatCompPoints } from '../../helpers';
import type { BonusShopProduct, BonusShopProductVersion } from '../../types';
import { ScBonusShopCardLabel } from '../BonusShopCard/styled';
import { ScBonusShopTermsText } from '../BonusShopTerms/styled';
import {
  ScExchangeModal,
  ScExchangeModalFooter,
  ScExchangeModalRows,
} from './styled';

interface BonusShopExchangeModalProps {
  product: BonusShopProduct;
  version?: BonusShopProductVersion;
  balance: number;
  isPending?: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

const IMAGE_SIZE = 88;
const TERMS_MAX_WIDTH = 640;

export const BonusShopExchangeModal: FC<BonusShopExchangeModalProps> = ({
  product,
  version,
  balance,
  isPending,
  onClose,
  onConfirm,
}) => {
  const { t } = useTranslation('bonusShop');
  const [isTermsOpen, setIsTermsOpen] = useState(false);

  const isPremium = product.type === BonusShopProductType.PREMIUM;
  const cpPrice = version ? version.cpPrice : product.cpPrice;
  const details = version ? version.details : product.details;
  const productTerms = version ? version.productTerms : product.productTerms;
  const title = version ? `${version.label} ${product.title}` : product.title;
  const hasEnoughPoints = balance >= cpPrice;

  return (
    <Modal isPortal padding={16}>
      <ScExchangeModal $isPremium={isPremium}>
        <div className="modal-head">
          <p className="modal-title">{t('modal.title')}</p>
          <ButtonClose isStatic onClick={onClose} />
        </div>

        <div className="modal-body">
          {!hasEnoughPoints && (
            <WarningMessage message={t('modal.notEnoughPoints')} />
          )}

          <div className="product">
            <ImageComponent
              alt={product.title}
              blurDataURL={blurImagePlaceholder}
              height={IMAGE_SIZE}
              placeholder="blur"
              src={product.img?.url || blurImagePlaceholder}
              width={IMAGE_SIZE}
            />
            <div className="product-info">
              {isPremium && <p className="product-type">{t('premium')}</p>}
              <p className="product-title">{title}</p>
            </div>
            {product.label && (
              <ScBonusShopCardLabel
                $inline
                $label={product.label}
                className="product-label"
              >
                {t(`labels.${product.label}`)}
              </ScBonusShopCardLabel>
            )}
          </div>

          {isPremium && product.amount && (
            <InfoMessage
              message={t('modal.premiumInfo', { amount: product.amount })}
            />
          )}

          {!!details.length && (
            <ScExchangeModalRows>
              {details.map(({ label, value }) => (
                <div className="row" key={label}>
                  <span className="row-label">{label}</span>
                  <span className="row-value">{value}</span>
                </div>
              ))}
            </ScExchangeModalRows>
          )}

          <ScExchangeModalRows>
            <div className="row accent">
              <span className="row-label">{t('modal.price')}</span>
              <span className="row-value">{formatCompPoints(cpPrice)} CP</span>
            </div>
            <div className="row">
              {isPremium ? (
                <>
                  <span className="row-label">{t('modal.youWillReceive')}</span>
                  <span className="success row-value">
                    {`${product.amount} CASH`}
                  </span>
                </>
              ) : (
                <>
                  <span className="row-label">{t('modal.currentBalance')}</span>
                  <span className="row-value">
                    {formatCompPoints(balance)} CP
                  </span>
                </>
              )}
            </div>
          </ScExchangeModalRows>
        </div>

        <ScExchangeModalFooter>
          <div className="actions">
            <Button onClick={onClose} whiteTransparent>
              {t('modal.cancel')}
            </Button>
            <Button
              disabled={!hasEnoughPoints}
              isLoading={isPending}
              onClick={onConfirm}
              primary
            >
              {t('modal.confirm')}
            </Button>
          </div>
          <p className="terms">
            <Trans
              components={{
                btn: (
                  <button onClick={() => setIsTermsOpen(true)} type="button" />
                ),
              }}
              i18nKey="modal.terms"
              ns="bonusShop"
            />
          </p>
        </ScExchangeModalFooter>
      </ScExchangeModal>

      {isTermsOpen && (
        <StaticModal
          dark
          isPortal
          maxWidth={TERMS_MAX_WIDTH}
          onClose={() => setIsTermsOpen(false)}
          title={t('modal.productTerms')}
        >
          <ScBonusShopTermsText
            dangerouslySetInnerHTML={{ __html: productTerms }}
          />
        </StaticModal>
      )}
    </Modal>
  );
};
